import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CardChecklistItemModel,
  CardChecklistListModel,
  CardChecklistModel,
  CreateCardChecklistRequestModel,
  ReorderCardChecklistsRequestModel,
  UpdateCardChecklistRequestModel,
} from '../models/card-details-api.models';
import { asArray, asNumber, asRecord, asString } from './card-api.utils';

@Injectable({ providedIn: 'root' })
export class CardChecklistsApiService {
  private readonly http = inject(HttpClient);

  async getByCard(cardId: string): Promise<CardChecklistListModel> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/cards/${cardId}/checklists`)
    );
    const items = this.extractChecklistArray(response)
      .map((entry) => this.mapChecklist(entry, cardId))
      .filter((entry) => !!entry.id);
    return { items };
  }

  async create(cardId: string, payload: CreateCardChecklistRequestModel): Promise<CardChecklistModel | null> {
    const response = await firstValueFrom(this.http.post<unknown>(`/api/cards/${cardId}/checklists`, payload));
    const mapped = this.mapChecklist(this.extractChecklistEntity(response), cardId);
    return mapped.id ? mapped : null;
  }

  async update(
    cardId: string,
    checklistId: string,
    payload: UpdateCardChecklistRequestModel
  ): Promise<void> {
    await firstValueFrom(
      this.http.put(`/api/cards/${cardId}/checklists/${checklistId}`, payload)
    );
  }

  async delete(cardId: string, checklistId: string): Promise<void> {
    await firstValueFrom(this.http.delete(`/api/cards/${cardId}/checklists/${checklistId}`));
  }

  async reorder(cardId: string, payload: ReorderCardChecklistsRequestModel): Promise<void> {
    await firstValueFrom(this.http.put(`/api/cards/${cardId}/checklists/reorder`, payload));
  }

  private mapChecklist(raw: unknown, cardId: string): CardChecklistModel {
    const source = asRecord(raw);
    const id = this.readString(source, ['id', 'Id']);
    return {
      id,
      cardId: this.readString(source, ['cardId', 'CardId']) || cardId,
      title: this.readString(source, ['title', 'Title']),
      order: this.readNumber(source, ['order', 'Order'], 0),
      items: this.mapItems(source['items'] ?? source['Items'], id),
    };
  }

  private mapItems(raw: unknown, checklistId: string): CardChecklistItemModel[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    return raw
      .map((entry) => asRecord(entry))
      .map((entry, index) => ({
        id: this.readString(entry, ['id', 'Id']),
        cardChecklistId:
          this.readString(entry, ['cardChecklistId', 'cardChecklistID', 'CardChecklistId']) ||
          checklistId,
        title: this.readString(entry, ['title', 'Title']),
        isCompleted: this.readBoolean(entry, ['isCompleted', 'IsCompleted']),
        order: this.readNumber(entry, ['order', 'Order'], index + 1),
        completedAt: this.readNullableString(entry, ['completedAt', 'CompletedAt']),
        completedByUserId: this.readNullableString(entry, ['completedByUserId', 'CompletedByUserId']),
      }))
      .filter((item) => !!item.id);
  }

  private extractChecklistArray(raw: unknown): unknown[] {
    if (Array.isArray(raw)) return raw;
    const root = asRecord(raw);
    const data = asRecord(root['data']);

    const candidates: unknown[] = [
      root['items'],
      root['checklists'],
      root['data'],
      data['items'],
      data['checklists'],
      data['data'],
      root['result'],
      data['result'],
    ];

    for (const candidate of candidates) {
      if (!Array.isArray(candidate)) continue;
      // Evita confundir "items de checklist" com "lista de checklists".
      const valid = candidate.filter((entry) => this.isChecklistEntity(entry));
      if (valid.length > 0) return valid;
    }

    // Alguns backends podem retornar checklist unico em vez de lista.
    if (this.isChecklistEntity(root)) return [root];
    if (this.isChecklistEntity(data)) return [data];

    const result = asRecord(root['result']);
    if (this.isChecklistEntity(result)) return [result];

    const nestedResult = asRecord(data['result']);
    if (this.isChecklistEntity(nestedResult)) return [nestedResult];

    return [];
  }

  private isChecklistEntity(raw: unknown): boolean {
    const entry = asRecord(raw);
    const id = this.readString(entry, ['id', 'Id']);
    if (!id) return false;

    // Item de checklist costuma ter cardChecklistId.
    const cardChecklistId = this.readString(entry, ['cardChecklistId', 'CardChecklistId']);
    if (cardChecklistId) return false;

    // Checklist deve ter ao menos cardId/title/order em algum formato.
    return Boolean(
      this.readString(entry, ['cardId', 'CardId']) ||
      this.readString(entry, ['title', 'Title']) ||
      typeof (entry['order'] ?? entry['Order']) === 'number' ||
      typeof (entry['order'] ?? entry['Order']) === 'string'
    );
  }

  private extractChecklistEntity(raw: unknown): unknown {
    if (Array.isArray(raw)) return raw.find((entry) => this.isChecklistEntity(entry)) ?? {};
    const root = asRecord(raw);
    const data = asRecord(root['data']);

    const candidates: unknown[] = [
      root,
      root['item'],
      root['checklist'],
      root['result'],
      data,
      data['item'],
      data['checklist'],
      data['result'],
    ];

    for (const candidate of candidates) {
      if (this.isChecklistEntity(candidate)) return candidate;
    }

    return root;
  }

  private readString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = asString(source[key]);
      if (value) return value;
    }
    return '';
  }

  private readNullableString(source: Record<string, unknown>, keys: string[]): string | null {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string') return value;
    }
    return null;
  }

  private readNumber(source: Record<string, unknown>, keys: string[], fallback: number): number {
    for (const key of keys) {
      const raw = source[key];
      if (typeof raw === 'number') return raw;
      if (typeof raw === 'string' && raw.trim()) {
        const parsed = Number(raw);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    return fallback;
  }

  private readBoolean(source: Record<string, unknown>, keys: string[]): boolean {
    for (const key of keys) {
      const raw = source[key];
      if (typeof raw === 'boolean') return raw;
    }
    return false;
  }
}
